import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiSearch, FiArrowLeft,
  FiCheck, FiXCircle, FiEye, FiClock, FiPrinter, FiRefreshCw, FiCornerDownLeft
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../config/firebase';
import { recomputeRecoveryFromLivePayrolls } from '../../utils/payrollHelper';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton,
  LoadingMessage, TranEmptyState, TableContainer, Table, Th, Tr, Td,
  StatusBadge, ActionGroup, IconBtn,
  FormHeader, BackButton, FormTitle, FormActions, SaveButton, FormCard,
  SectionTitle, FormGrid, FormGroup, Label, Input, Select, TextArea
} from '../../components/SharedStyles';
import {
  createTransaction, updateTransaction, deleteTransaction,
  getTransactions, updateTransactionStatus
} from '../../utils/transactionHelper';
import { printDocument, loadDocumentFormats } from '../../utils/printTemplates';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useCurrency from '../../hooks/useCurrency';
import useOpenFromReport from '../../hooks/useOpenFromReport';
import { isFutureDate, isLargeTransaction, LARGE_TRANSACTION_THRESHOLD } from '../../utils/validationHelpers';
import { formatAmount } from '../../utils/documentNumbering';

const TRAN_TYPE_CODE = 'ADV';
const STATUS_COLORS = {
  Draft: colors.warning.main,
  Issued: colors.info.main,
  Recovered: colors.success.main,
  Cancelled: colors.error.main,
};

const SalaryAdvance = () => {
  const { activeCompany, activeFinancialYear, currentUser, activeBranch, isModuleEnabled } = useAuth();
  const { canEdit, canDelete } = usePagePermissions('salary', 'advance');
  // Advance paid back in cash / bank → opens a Loan Recovery voucher for it.
  const { canEdit: canRecover } = usePagePermissions('salary', 'loanRecovery');
  const recoveryOn = !!(isModuleEnabled && isModuleEnabled('salary_loan_recovery'));
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();
  const showBranchCol = activeBranch && activeBranch.id === 'all';

  const [viewMode, setViewMode] = useState('list');
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    docDate: new Date().toISOString().split('T')[0],
    partyId: '', partyName: '', partyAccountId: '', partyAccountCode: '',
    amount: 0,
    paymentMethod: 'cash',  // 'cash' | 'bank'
    paymentAccountId: '', paymentAccountCode: '', paymentAccountName: '',
    reason: '', notes: '',
    status: 'Draft'
  });

  const [staffList, setStaffList] = useState([]);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);

  // Print config (per-company document format overrides)
  const [printConfig, setPrintConfig] = useState(null);
  useEffect(() => {
    if (activeCompany?.id) loadDocumentFormats(activeCompany.id).then(setPrintConfig);
  }, [activeCompany]);

  const handlePrint = (adv) => {
    printDocument('salaryAdvance', adv, activeCompany, printConfig, activeBranch);
  };

  // ─── Load data ───
  const loadAdvances = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    try {
      const txs = await getTransactions(activeCompany.id, {
        tranTypeCode: TRAN_TYPE_CODE,
        ...(activeBranch && activeBranch.id !== 'all' ? { branchId: activeBranch.id } : {})
      });
      setAdvances(txs);
    } catch (e) { console.error('Load ADV error:', e); }
    finally { setLoading(false); }
  }, [activeCompany, activeBranch]);

  const loadLookups = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const [staffSnap, accSnap] = await Promise.all([
        // Load all staff, filter client-side (avoids needing a composite index on isActive+staffCode)
        getDocs(query(
          collection(db, 'companies', activeCompany.id, 'salaryStaff'),
          orderBy('staffCode')
        )),
        getDocs(collection(db, 'companies', activeCompany.id, 'accounts'))
      ]);
      setStaffList(
        staffSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(s => s.isActive !== false)
      );

      const allAccs = accSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setCashAccounts(allAccs.filter(a => a.level === 5 && a.code?.startsWith('11101') && a.isActive !== false)
        .sort((a, b) => (a.code || '').localeCompare(b.code || '')));
      setBankAccounts(allAccs.filter(a => a.level === 5 && a.code?.startsWith('11102') && a.isActive !== false)
        .sort((a, b) => (a.code || '').localeCompare(b.code || '')));
    } catch (e) { console.error('Load lookups error:', e); }
  }, [activeCompany]);

  useEffect(() => { loadAdvances(); loadLookups(); }, [loadAdvances, loadLookups]);

  // ─── Form helpers ───
  const resetForm = () => {
    setEditing(null);
    setFormData({
      docDate: new Date().toISOString().split('T')[0],
      partyId: '', partyName: '', partyAccountId: '', partyAccountCode: '',
      amount: 0,
      paymentMethod: 'cash',
      paymentAccountId: '', paymentAccountCode: '', paymentAccountName: '',
      reason: '', notes: '',
      status: 'Draft'
    });
  };

  const openNew = () => {
    if (!activeFinancialYear?.id) { alert('Please activate a financial year first.'); return; }
    if (activeBranch && activeBranch.id === 'all') {
      alert('Please pick a specific branch before creating an Advance.');
      return;
    }
    resetForm();
    setViewMode('form');
  };

  const openEdit = (adv) => {
    setEditing(adv);
    setFormData({
      docDate: adv.docDate || '',
      partyId: adv.partyId || '',
      partyName: adv.partyName || '',
      partyAccountId: adv.partyAccountId || '',
      partyAccountCode: adv.partyAccountCode || '',
      amount: adv.totalAmount || 0,
      paymentMethod: adv.paymentMethod || 'cash',
      paymentAccountId: adv.paymentAccountId || '',
      paymentAccountCode: adv.paymentAccountCode || '',
      paymentAccountName: adv.paymentAccountName || '',
      reason: adv.reason || '', notes: adv.notes || '',
      status: adv.status || 'Draft'
    });
    setViewMode('form');
  };

  const goBack = () => { setViewMode('list'); resetForm(); };

  useOpenFromReport(advances, openEdit);

  const handleStaffChange = (staffId) => {
    const s = staffList.find(x => x.id === staffId);
    setFormData(prev => ({
      ...prev,
      partyId: staffId,
      partyName: s?.fullName || '',
      partyAccountId: s?.linkedAccountId || '',
      partyAccountCode: ''   // resolved by getPartyAccount on save (defensive)
    }));
  };

  const handleAccountChange = (accountId) => {
    const list = formData.paymentMethod === 'cash' ? cashAccounts : bankAccounts;
    const acc = list.find(a => a.id === accountId);
    setFormData(prev => ({
      ...prev,
      paymentAccountId: accountId,
      paymentAccountCode: acc?.code || '',
      paymentAccountName: acc?.name || ''
    }));
  };

  const handlePaymentMethodChange = (method) => {
    setFormData(prev => ({
      ...prev,
      paymentMethod: method,
      paymentAccountId: '', paymentAccountCode: '', paymentAccountName: ''
    }));
  };

  // ─── Validation + Save ───
  const validate = () => {
    if (!formData.partyId) return 'Select a Salary Staff member.';
    if (!formData.partyAccountId) return 'Selected staff has no linked COA account. Edit the staff record and re-save to create one.';
    if (Number(formData.amount) <= 0) return 'Amount must be greater than zero.';
    if (!formData.paymentAccountId) return 'Select a payment account (Cash or Bank).';
    return null;
  };

  const buildTransactionData = (status) => ({
    tranTypeCode: TRAN_TYPE_CODE,
    docDate: formData.docDate,
    financialYearId: activeFinancialYear?.id,
    branchId: activeBranch?.id || null,
    branchName: activeBranch?.branchName || null,
    partyType: 'staff',
    partyId: formData.partyId,
    partyName: formData.partyName,
    partyAccountId: formData.partyAccountId,
    partyAccountCode: formData.partyAccountCode,
    paymentMethod: formData.paymentMethod,
    paymentAccountId: formData.paymentAccountId,
    paymentAccountCode: formData.paymentAccountCode,
    paymentAccountName: formData.paymentAccountName,
    reason: formData.reason,
    notes: formData.notes,
    // Force totals (no lines, no tax — direct amount)
    subtotal: Number(formData.amount) || 0,
    discountAmount: 0,
    taxableAmount: Number(formData.amount) || 0,
    taxAmount: 0,
    whtRate: 0, whtAmount: 0,
    totalAmount: Number(formData.amount) || 0,
    // Recovery tracking (initialized; updated by Payroll Run in Phase 5)
    recoveredAmount: editing?.recoveredAmount || 0,
    outstandingAmount: (Number(formData.amount) || 0) - (editing?.recoveredAmount || 0),
    status
  });

  const runWarnings = (status) => {
    if (isFutureDate(formData.docDate)) {
      if (!window.confirm('Document date is in the future. Continue?')) return false;
    }
    if (isLargeTransaction(Number(formData.amount))) {
      if (!window.confirm(`Amount Rs ${formatAmount(formData.amount)} exceeds Rs ${formatAmount(LARGE_TRANSACTION_THRESHOLD)}. Continue?`)) return false;
    }
    return true;
  };

  const handleSave = async (status = 'Draft') => {
    if (!canEdit) { alert('No permission to save.'); return; }
    const err = validate();
    if (err) { alert(err); return; }
    if (!runWarnings(status)) return;

    setSaving(true);
    try {
      const data = buildTransactionData(status);
      let newTranData = null;
      if (editing) {
        await updateTransaction(activeCompany.id, editing.id, data, currentUser.uid);
      } else {
        newTranData = await createTransaction(activeCompany.id, data, currentUser.uid);
      }

      // Optimistic UI update — show the saved record immediately so the Save
      // button releases without waiting on a full collection re-fetch.
      const savedId = editing ? editing.id : newTranData?.id;
      const savedRecord = editing
        ? { ...editing, ...data, id: savedId, status }
        : { ...newTranData, status };
      setAdvances(prev => editing
        ? prev.map(r => r.id === savedId ? savedRecord : r)
        : [savedRecord, ...prev]);

      goBack();

      // Background refresh — reconciles with server data without blocking UI.
      loadAdvances().catch(err => console.error('Background refresh failed:', err));
    } catch (e) {
      console.error('Save ADV error:', e);
      alert('Failed: ' + (e?.message || ''));
    } finally { setSaving(false); }
  };

  const handleStatusChange = async (adv, newStatus) => {
    if (!canEdit) { alert('No permission.'); return; }
    if (!window.confirm(`Change ${adv.docNumber} status to "${newStatus}"?`)) return;
    try {
      await updateTransactionStatus(activeCompany.id, adv.id, newStatus, currentUser.uid);
      await loadAdvances();
    } catch (e) { alert('Failed: ' + (e?.message || '')); }
  };

  const handleDelete = async (adv) => {
    if (!canDelete) { alert('No permission to delete.'); return; }
    if ((adv.recoveredAmount || 0) > 0) {
      alert(`Cannot delete: ${formatCurrency(adv.recoveredAmount)} has already been recovered via payroll. Cancel instead.`);
      return;
    }
    if (!window.confirm(`Delete ${adv.docNumber}? This removes the journal + stock entries.`)) return;
    try {
      await deleteTransaction(activeCompany.id, adv.id);
      await loadAdvances();
    } catch (e) { alert('Failed: ' + (e?.message || '')); }
  };

  // Recalculate a stuck advance's recovery from the payrolls that still exist. If the
  // recovering payroll was deleted, recovered → 0 and the advance unlocks. Only fixes
  // tracking fields — the ledger stays as-is.
  const handleUnlock = async (adv) => {
    if (!canDelete) { alert('No permission.'); return; }
    if (!window.confirm(`Recalculate recovery for ${adv.docNumber}?\n\nCurrently shows ${formatCurrency(adv.recoveredAmount || 0)} recovered. This resets it from the payrolls and Loan Recovery vouchers that still exist — if none, it unlocks the advance for editing/deleting.`)) return;
    try {
      const res = await recomputeRecoveryFromLivePayrolls(activeCompany.id, adv);
      await loadAdvances();
      alert(`Recovery recalculated: now ${formatCurrency(res.recovered)} recovered.`);
    } catch (e) { alert('Failed: ' + (e?.message || '')); }
  };

  useDocumentShortcuts({
    onNew: openNew,
    onSave: () => handleSave(formData.status === 'Issued' ? 'Issued' : 'Draft'),
    viewMode,
    saving
  });

  const filtered = advances.filter(a => {
    const m = !searchQuery ||
      a.docNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.partyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.reason?.toLowerCase().includes(searchQuery.toLowerCase());
    const s = statusFilter === 'All' || a.status === statusFilter;
    return m && s;
  });

  // ═══════════════════════════════════════════════════════════
  if (viewMode === 'list') {
    return (
      <PageLayout pageTitle="Salary Advance" breadcrumb="Salary / Advance">
        <ActionBar>
          <SearchBox>
            <FiSearch />
            <input type="text" placeholder="Search doc#, staff, reason..."
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
          </SearchBox>
          <FilterGroup>
            <StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="All">All Status</option>
              {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
            </StatusFilterSelect>
          </FilterGroup>
          {canEdit && <TranAddButton onClick={openNew}><FiPlus /> New Advance</TranAddButton>}
        </ActionBar>

        {loading ? <LoadingMessage>Loading advances...</LoadingMessage>
          : filtered.length === 0 ? (
            <TranEmptyState>
              {searchQuery || statusFilter !== 'All'
                ? 'No advances match your filters.'
                : 'No salary advances yet. Click "New Advance" to issue one.'}
            </TranEmptyState>
          ) : (
            <TableContainer>
              <Table>
                <thead><tr>
                  <Th>Doc #</Th><Th>Date</Th><Th>Staff</Th><Th>Reason</Th>
                  <Th style={{ textAlign: 'right' }}>Amount</Th>
                  <Th style={{ textAlign: 'right' }}>Recovered</Th>
                  <Th style={{ textAlign: 'right' }}>Outstanding</Th>
                  <Th>Paid From</Th>
                  {showBranchCol && <Th>Branch</Th>}
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </tr></thead>
                <tbody>
                  {filtered.map(a => (
                    <Tr key={a.id}>
                      <Td><strong>{a.docNumber}</strong></Td>
                      <Td>{a.docDate}</Td>
                      <Td>{a.partyName || '-'}</Td>
                      <Td style={{ color: colors.text.secondary, fontSize: 12 }}>
                        {a.reason ? (a.reason.length > 40 ? a.reason.slice(0, 40) + '…' : a.reason) : '-'}
                      </Td>
                      <Td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(a.totalAmount || 0)}</Td>
                      <Td style={{ textAlign: 'right', color: colors.success.dark }}>
                        {formatCurrency(a.recoveredAmount || 0)}
                        {(a.voucherRecoveredAmount || 0) > 0 && (
                          <div style={{ fontSize: 10, color: colors.text.secondary, fontWeight: 400 }}>incl. {formatCurrency(a.voucherRecoveredAmount)} by voucher</div>
                        )}
                      </Td>
                      <Td style={{ textAlign: 'right', color: (a.outstandingAmount || 0) > 0 ? colors.warning.dark : colors.text.tertiary }}>
                        {formatCurrency(a.outstandingAmount ?? a.totalAmount ?? 0)}
                      </Td>
                      <Td>
                        <PayBadge $type={a.paymentMethod}>{(a.paymentMethod || 'cash').toUpperCase()}</PayBadge>
                        <small style={{ marginLeft: 4, color: colors.text.secondary }}>{a.paymentAccountName || ''}</small>
                      </Td>
                      {showBranchCol && <Td>{a.branchName || '-'}</Td>}
                      <Td>
                        <StatusBadge style={{ background: STATUS_COLORS[a.status] + '20', color: STATUS_COLORS[a.status] }}>
                          {a.status}
                        </StatusBadge>
                      </Td>
                      <Td>
                        <ActionGroup>
                          <IconBtn title="View" onClick={() => openEdit(a)}><FiEye /></IconBtn>
                          {canEdit && (a.recoveredAmount || 0) === 0 && (
                            <IconBtn title="Edit" onClick={() => openEdit(a)}><FiEdit2 /></IconBtn>
                          )}
                          {canEdit && a.status === 'Draft' && (
                            <IconBtn title="Issue (post journal)" onClick={() => handleStatusChange(a, 'Issued')}><FiCheck /></IconBtn>
                          )}
                          {canEdit && a.status === 'Issued' && (a.recoveredAmount || 0) === 0 && (
                            <IconBtn title="Cancel" onClick={() => handleStatusChange(a, 'Cancelled')}><FiXCircle /></IconBtn>
                          )}
                          {recoveryOn && canRecover && a.status === 'Issued' && (a.outstandingAmount ?? a.totalAmount ?? 0) > 0 && (
                            <IconBtn title="Recover — paid back in cash / bank"
                              onClick={() => navigate('/salary/loan-recovery', { state: { recoverFor: { staffId: a.partyId, transactionId: a.id } } })}>
                              <FiCornerDownLeft />
                            </IconBtn>
                          )}
                          <IconBtn title="Print" onClick={() => handlePrint(a)}><FiPrinter /></IconBtn>
                          {canDelete && (a.recoveredAmount || 0) > 0 && (
                            <IconBtn title="Recalculate / unlock recovery" onClick={() => handleUnlock(a)}><FiRefreshCw /></IconBtn>
                          )}
                          {canDelete && (a.recoveredAmount || 0) === 0 && (
                            <IconBtn $danger title="Delete" onClick={() => handleDelete(a)}><FiTrash2 /></IconBtn>
                          )}
                        </ActionGroup>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          )}
      </PageLayout>
    );
  }

  // ═══════════════════════════════════════════════════════════ FORM
  const accountList = formData.paymentMethod === 'cash' ? cashAccounts : bankAccounts;

  return (
    <PageLayout pageTitle="Salary Advance" breadcrumb="Salary / Advance">
      <FormHeader>
        <BackButton onClick={goBack}><FiArrowLeft /> Back</BackButton>
        <FormTitle>{editing ? `Edit ${editing.docNumber}` : 'New Salary Advance'}</FormTitle>
        <FormActions>
          {editing && (
            <SaveButton type="button" onClick={() => handlePrint(editing)}
              style={{ background: colors.text.secondary }}>
              <FiPrinter /> Print
            </SaveButton>
          )}
          <SaveButton type="button" onClick={() => handleSave('Draft')} disabled={saving}>
            Save Draft
          </SaveButton>
          <SaveButton type="button" onClick={() => handleSave('Issued')} disabled={saving}
            style={{ background: colors.success.main }}>
            {saving ? 'Saving...' : 'Save & Issue'}
          </SaveButton>
        </FormActions>
      </FormHeader>

      <FormCard>
        <SectionTitle>Advance Details</SectionTitle>
        <FormGrid>
          <FormGroup>
            <Label>Doc Date *</Label>
            <Input type="date" value={formData.docDate} onChange={e => setFormData({...formData, docDate: e.target.value})} />
          </FormGroup>
          <FormGroup>
            <Label>Salary Staff *</Label>
            <Select value={formData.partyId} onChange={e => handleStaffChange(e.target.value)}>
              <option value="">— Select Staff —</option>
              {staffList.map(s => (
                <option key={s.id} value={s.id}>{s.staffCode} — {s.fullName}</option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>Amount *</Label>
            <Input type="number" min="0" step="0.01"
              value={formData.amount} onChange={e => setFormData({...formData, amount: e.target.value})} />
          </FormGroup>
          <FormGroup>
            <Label>Pay From</Label>
            <RadioRow>
              <label>
                <input type="radio" name="payMethod" checked={formData.paymentMethod === 'cash'}
                  onChange={() => handlePaymentMethodChange('cash')} /> Cash
              </label>
              <label>
                <input type="radio" name="payMethod" checked={formData.paymentMethod === 'bank'}
                  onChange={() => handlePaymentMethodChange('bank')} /> Bank
              </label>
            </RadioRow>
          </FormGroup>
          <FormGroup style={{ gridColumn: 'span 2' }}>
            <Label>{formData.paymentMethod === 'cash' ? 'Cash Account' : 'Bank Account'} *</Label>
            <Select value={formData.paymentAccountId} onChange={e => handleAccountChange(e.target.value)}>
              <option value="">— Select {formData.paymentMethod === 'cash' ? 'Cash' : 'Bank'} Account —</option>
              {accountList.map(a => (
                <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup style={{ gridColumn: 'span 2' }}>
            <Label>Reason</Label>
            <Input value={formData.reason} onChange={e => setFormData({...formData, reason: e.target.value})}
              placeholder="e.g. Medical emergency, festival, etc." />
          </FormGroup>
          <FormGroup style={{ gridColumn: 'span 4' }}>
            <Label>Notes</Label>
            <TextArea rows={2} value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} />
          </FormGroup>
        </FormGrid>
      </FormCard>

      <SummaryCard>
        <SummaryItem>
          <span>Staff</span>
          <strong>{formData.partyName || '—'}</strong>
        </SummaryItem>
        <SummaryItem>
          <span>Pay From</span>
          <strong>{formData.paymentAccountName || '—'}</strong>
        </SummaryItem>
        <SummaryItem $highlight>
          <span>Advance Amount</span>
          <strong>{formatCurrency(Number(formData.amount) || 0)}</strong>
        </SummaryItem>
      </SummaryCard>

      <InfoNote>
        <strong>How recovery works:</strong> when this advance is <em>Issued</em>, the system posts <code>Dr Staff Account / Cr {formData.paymentMethod === 'cash' ? 'Cash' : 'Bank'}</code>.
        The full amount auto-deducts from the staff's next Payroll Run (you can edit/skip the deduction on that run if needed).
        Status flips to <em>Recovered</em> automatically when the outstanding hits zero.
      </InfoNote>
    </PageLayout>
  );
};

// ─── Local Styles ───
const RadioRow = styled.div`
  display: flex; gap: ${spacing.lg}; align-items: center;
  padding: ${spacing.sm} ${spacing.md};
  border: 1px solid ${colors.border.light}; border-radius: ${borderRadius.sm};
  background: ${colors.background.default};
  label { display: flex; align-items: center; gap: 6px; cursor: pointer; font-size: ${typography.fontSize.sm}; }
  input { cursor: pointer; }
`;

const PayBadge = styled.span`
  display: inline-block; padding: 2px 8px;
  background: ${props => props.$type === 'bank' ? colors.info.main + '20' : colors.warning.main + '20'};
  color: ${props => props.$type === 'bank' ? colors.info.dark : colors.warning.dark};
  border-radius: ${borderRadius.sm}; font-size: 10px; font-weight: 700;
`;

const SummaryCard = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing.md};
  padding: ${spacing.md};
  background: ${colors.neutral[50]};
  border-radius: ${borderRadius.lg};
  margin-top: ${spacing.md};
`;
const SummaryItem = styled.div`
  padding: ${spacing.sm} ${spacing.md};
  background: ${props => props.$highlight ? colors.primary.main : 'white'};
  color: ${props => props.$highlight ? 'white' : colors.text.primary};
  border-radius: ${borderRadius.sm};
  display: flex; flex-direction: column; gap: 4px;
  span { font-size: 11px; opacity: 0.85; text-transform: uppercase; letter-spacing: 0.5px; }
  strong { font-size: ${typography.fontSize.lg}; font-family: monospace; }
`;
const InfoNote = styled.div`
  margin-top: ${spacing.md};
  padding: ${spacing.md};
  background: ${colors.info.main}10;
  border-left: 3px solid ${colors.info.main};
  border-radius: ${borderRadius.sm};
  color: ${colors.text.secondary};
  font-size: 12px;
  line-height: 1.6;
  code {
    background: white; padding: 1px 6px;
    border-radius: 3px; font-family: monospace;
    color: ${colors.primary.main};
  }
`;

export default SalaryAdvance;
